<template>
  <div class="waiting-list">
    <button
      type="button"
      class="waiting-list__button"
      v-on:click="dispatchCreateWaiting">Добавить запись</button>
    <button
      class="waiting-list__link"
      type="button"
      v-on:click="onViewWaitingList"
    >
      Смотреть все записи
    </button>
    <button
      type="button"
      class="waiting-list__item"
      v-for="item in waitings"
      v-bind:key="item.id"
      v-on:click="dispatchViewWaiting(item.id)">
      <div class="waiting-list__item__row">
        <p class="waiting-list__item__patient-name">
          <b>
            {{item.patientName}}
          </b>
          <span
            v-if="item.isArchive"
            class="waiting-list__icon-archive"
            title="Пациент в архиве"
          />
        </p>
        <button
          type="button"
          title="Редактировать"
          class="waiting-list__icon-edit"
          v-on:click.stop="dispatchViewWaiting(item.id)" />
        <button
          type="button"
          title="Развернуть"
          class="waiting-list__icon-checked"
          v-bind:class="{ active: item.shownDetails }"
          v-on:click.stop="onToggleDetails(item.id)" />
        <button
          v-if="canDelete"
          title="Удалить"
          type="button"
          class="waiting-list__icon-delete"
          v-on:click.stop="dispatchDeleteWaiting(item.id)" />
      </div>
      <template v-if="item.phone">
        <button
          v-if="canInitiateCalls"
          class="waiting-list__phone"
          title="Начать звонок"
          type="button"
          v-on:click.stop="onInitiateCall(item)"
        >{{item.phone}}</button>
        <p v-else>{{item.phone}}</p>
      </template>
      <div class="waiting-list__status" v-if="item.status === 'appointed'">
        Назначен
      </div>
      <br>
      <p><span>до </span>{{item.dateEnds}}</p>
      <p v-if="item.isShownEmployee"><span>Врач: </span>{{item.employeeName}}</p>
      <template v-else>
        <p
          v-for="(service, key) in item.services"
          :key="key"
        >
          <span>Услуга: </span>{{service.name}}
        </p>
      </template>
      <div v-if="item.shownDetails">
        <template v-if="item.isShownEmployee">
          <template v-if="item.services.length > 0">
            <p
              v-for="(service, key) in item.services"
              :key="key"
            >
              <span>Услуга: </span>{{service.name}}
            </p>
          </template>
          <template v-else>
            <p>
              <span>Услуга: </span>-
            </p>
          </template>
        </template>
        <p>
          <span>Время: c </span>{{item.timeStarts}}
          <span>до </span>{{item.timeEnds}}
        </p>
        <p><span>Комментарий: </span>{{item.comment || '-'}}</p>
      </div>
      <button
        class="waiting-list__make"
        type="button"
        v-on:click.stop="dispatchMakeAppointment(item.id, item.isArchive)">Записать</button>
    </button>
  </div>
</template>

<script>
import notifierMixin from '@/mixins/notifierMixin';
import dateMixin from '@/mixins/dateMixin';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import { GOAL_VIEW_WAITING_LIST } from '@/plugins/metric';

/**
 * @description Действие инициировать звонки.
 * @type {string}
 */
const ACTION_INITIATE_CALLS = 'initiate-calls';
/**
 * @description Действие удаления записи.
 * @type {string}
 */
const ACTION_DELETE_WAITINGS = 'delete-waitings';
/**
 * @description Название события создания записи.
 * @type {string}
 */
const NAME_EVENT_CREATE_WAITING = 'create-waiting';
/**
 * @description Название события удаления записи.
 * @type {string}
 */
const NAME_EVENT_DELETE_WAITING = 'delete-waiting';
/**
 * @description Название события инициации звонка.
 * @type {string}
 */
const NAME_EVENT_INITIATE_CALL = 'initiate-call';
/**
 * @description Название события просмотра записи.
 * @type {string}
 */
const NAME_EVENT_VIEW_WAITING = 'view-waiting';

/**
 * @description Компонент списка записей из листа ожидания.
 * @type {Vue}
 */
export default {
  computed: {
    canInitiateCalls() {
      return this.actions.includes(ACTION_INITIATE_CALLS);
    },
    canDelete() {
      return this.actions.includes(ACTION_DELETE_WAITINGS);
    },
    /**
     * @description Записи.
     * @type {Array}
     */
    waitings() {
      return this.list.map((waiting) => ({
        comment: waiting.comment || '-',
        dateEnds: this.getShortDate(waiting.dateEnds),
        employeeName: waiting.employeeName || '-',
        id: waiting.id,
        isShownEmployee: waiting.employeeName !== '' || waiting.services.length === 0,
        patientId: waiting.patientId,
        patientName: waiting.patientName,
        phone: waiting.patientPhone,
        services: waiting.services,
        shownDetails: this.waitingIdsForViewDetails.includes(waiting.id),
        timeEnds: waiting.timeEnds,
        timeStarts: waiting.timeStarts,
        status: waiting.status,
        isArchive: waiting.patientIsArchive,
      }));
    },
  },
  data() {
    return {
      /**
       * @description Идентификаторы записей для которых надо показывать расширенную информацию.
       * @type {number[]}
       */
      waitingIdsForViewDetails: [],
    };
  },
  methods: {
    /**
     * @description Обработать событие просмотра листа ожидания
     * @returns {void}
     */
    onViewWaitingList() {
      this.reachGoal();
      this.$router.push({ name: 'ClinicPanelWaitingList' });
    },
    /**
     * @description Достичь цели
     * @returns {void}
     */
    reachGoal() {
      this.$goal(GOAL_VIEW_WAITING_LIST);
    },
    /**
     * @description Отправить событие создание записи.
     * @returns {void}
     */
    dispatchCreateWaiting() {
      this.$emit(NAME_EVENT_CREATE_WAITING);
    },
    /**
     * @description Отправить событие удаления записи.
     * @param {number} id
     * @returns {void}
     */
    async dispatchDeleteWaiting(id) {
      const dialog = await this.showRemoveDialog('Удалить запись из листа ожидания?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      this.$emit(NAME_EVENT_DELETE_WAITING, id);
    },
    /**
     * @description Отправить событие создания записи на прием из листа ожидания.
     * @param {number} id Идентификатор.
     * @param {boolean} isArchive Пациент архивный.
     * @returns {void}
     */
    dispatchMakeAppointment(id, isArchive) {
      if (isArchive) {
        this.notifyFail('Установлен запрет записи на прием');
        return;
      }
      this.$emit('make-appointment', id);
    },
    /**
     * @description Отправить событие просмотра записи.
     * @param {number} id
     * @returns {void}
     */
    dispatchViewWaiting(id) {
      this.$emit(NAME_EVENT_VIEW_WAITING, id);
    },
    /**
     * @description Переключить отображение дополнительных данных записи.
     * @param {number} waiting Запись.
     * @returns {void}
     */
    async onToggleDetails(waitingId) {
      const index = this.waitingIdsForViewDetails.indexOf(waitingId);
      if (index === -1) {
        this.waitingIdsForViewDetails.push(waitingId);
      } else {
        this.waitingIdsForViewDetails.splice(index, 1);
      }
    },
    /**
     * @description Обработать инициацию звонка.
     * @param {Object} waiting
     * @returns {void}
     */
    onInitiateCall(waiting) {
      this.$emit(NAME_EVENT_INITIATE_CALL, waiting.patientId, waiting.phone);
    },
  },
  props: {
    /**
     * @description Действия.
     */
    actions: { default: () => [], type: Array },
    /**
     * @description Список записей.
     */
    list: { required: true, type: Array },
  },
  name: 'WaitingList',
  mixins: [builtInDialogMixin, dateMixin, notifierMixin],
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.waiting-list {
  flex-direction: column;
  display: flex;
  gap: 9px;
  margin-top: 10px;
  padding: 0;
  overflow: auto;
  &__dialog {
    &__button {
      @include buttons.btn-transparent(50%, 2rem, 1rem);
      margin: 0 0 20px 0;
      &.negative {
        border: 1px solid rgba(224, 4, 4, 0.97);
        color: rgba(224, 4, 4, 0.97);
      }
    }
  }
  &__button {
    @include buttons.btn-blue(100%, 56px, 20px);
    @include buttons.btn-blue-add(21px);
    margin-bottom: 10px;
  }
  &__link {
    width: 160px;
    left: 0;
    color: #1976d2;
    text-decoration: 1px;
    background: none!important;
    border: none;
    padding: 0!important;
    text-decoration: underline;
    cursor: pointer;
    &:hover {
      text-decoration: none;
    }
  }
  &__item {
    text-align: left;
    padding: 10px 15px;
    background-color: #eaeff3;
    border-radius: 4px;
    transition: background-color var(--animate-duration);
    &:hover {
      background-color: var(--section-background-color);
      cursor: pointer;
    }
    &__patient-name {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    &__row {
      display: grid;
      grid-template-columns: 1fr 20px 20px 20px;
    }
    p {
      padding: 0;
      margin: 0 0 4px 0;
      font-weight: 400;
      font-size: 14px;
      line-height: 17px;
      color: var(--reading-color);
      b {
        font-weight: 700;
        font-size: 17px;
        line-height: 20px;
      }
    }
    span {
      color: #90a0b7;
      font-weight: 400;
      font-size: 14px;
      line-height: 17px;
    }
  }
  &__phone {
    color: var(--action-color);
    text-decoration: underline;
  }
  &__status {
    color: green;
  }
  &__icon {
    &-archive {
      display: inline-block;
      width: 16px;
      height: 16px;
      background-image: url("@/assets/img/archive-add-blue.svg");
      background-repeat: no-repeat;
      background-size: contain;
      background-position: center;
    }
    &-checked {
      display: block;
      align-self: center;
      background-image: url("@/assets/img/checked/checked-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 14px;
      height: 14px;
      padding: 0;
      margin: 4px 0 0 0;
      cursor: pointer;
      transition: transform var(--animate-duration);
      &:hover {
        background-image: url("@/assets/img/checked/checked-blue.svg");
      }
      &.active {
        margin: -8px 0 0 0;
        transform: rotate(180deg);
      }
    }
    &-edit {
      display: block;
      align-self: center;
      background-image: url("@/assets/img/pen-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 14px;
      height: 14px;
      padding: 0;
      margin: 0;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/pen-blue.svg");
      }
    }
    &-delete {
      display: block;
      align-self: center;
      background-image: url("@/assets/img/exit-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 12px;
      height: 12px;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/exit-blue.svg");
      }
    }
  }
  &__make {
    margin-top: 10px;
    @include buttons.btn-blue(100%, 2rem, 1rem);
  }
}
</style>
